import type { ComponentProps } from 'react';
import type { FullContest } from '@betterpool/api-types';

import type { ModalType } from '~/domains/common/context/RootModalsContext/types';
import type { SocureKycStatus } from '~/services/users/types';
import type { CheckRequestError, CheckRequestStatus } from '~/utils/geocomply';

import type LocationRestrictionModal from './Modals/LocationRestrictionModal';

// eslint-disable-next-line no-restricted-syntax
export enum ProtectionGuardType {
  USER,
  KYC,
  LOCATION,
  GEOCOMPLY,
  CONTEST,
}

type UserChecksProps = {
  isKYCloading: boolean;
  KYCStatus?: SocureKycStatus;
};

type LocationChecksProps = {
  allowedLocation?: boolean;
  isGeoComplyEnabled: boolean;
  currentCountry?: string;
  currentLocation?: string;
  geoPermissionStatus?: PermissionState;
  initialLocationLoaded?: boolean;
  locationError?: CheckRequestError;
  isLocationLoading: boolean;
  locationErrors?: CheckRequestError[];
  locationState?: string;
  locationStatus?: CheckRequestStatus;
  verifyingLocation?: boolean;
  userAge: number;
};

type GeoComplyChecksProps = LocationChecksProps & {
  refetchLocationToken: (forceRefetch?: boolean) => Promise<unknown>;
};

export type LocationRestrictionModalOverrides = Partial<
  Pick<
    ComponentProps<typeof LocationRestrictionModal>,
    'variant' | 'detectedLocation' | 'onRecheckLocation' | 'queryKey'
  >
>;

type ContestChecksProps = {
  contestData?: FullContest | undefined;
  geoPermissionStatus?: PermissionState;
  isContestLoading: boolean;
  isLocationLoading: boolean;
  isLocationRestrictedOverride: boolean;
  locationCountry?: string;
  locationRestrictionModal?: LocationRestrictionModalOverrides;
  locationState?: string;
};

export type AccessChecksProps<T extends ProtectionGuardType> = {
  // Shared
  action: string;
  allowAnnonymous: boolean;
  redirectTo: string;
  requireKycReturn?: boolean;
  userId: string;
  contestId: string;
  closeModalsByType: (modalType: string) => void;
  handleOnAccessFailed: (isSubmit: boolean) => void;
  openModalUnique: (modal: ModalType) => Promise<unknown>;
  forceRerender: () => void | Promise<boolean | void>;
} & (T extends ProtectionGuardType.USER | ProtectionGuardType.KYC
  ? UserChecksProps
  : T extends ProtectionGuardType.LOCATION
    ? LocationChecksProps
    : T extends ProtectionGuardType.GEOCOMPLY
      ? GeoComplyChecksProps
      : T extends ProtectionGuardType.CONTEST
        ? ContestChecksProps
        : never);
